/** @jsx jsx */
import {
  jsx,
  Button,
  Spinner,
  Container,
  useThemeUI,
  Grid,
  Box,
} from "theme-ui";
import { useCallback, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import donationsOfflineService from "sites-common/service-wrappers/donation-offline-service";
import submitDonationRequest from "sites-common/service-wrappers/donation-service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DynamicFieldStyle1 as InputWithIconAndLabel } from "sites-common/components/DynamicField";
import { usePersist } from "../../../globals";
import Tos from "../../Tos";
import validateProfile from "./DonorProfile/validateProfile";
import ProfileSelector from "./DonorProfile/ProfileSelector";

import ErrorsOrWarnings from "./ErrorsOrWarnings";
import EditProfile from "./DonorProfile/EditProfile";
import useDonorProfiles from "../hooks/useDonorProfiles";
import { checkRestrictions, DonationConfProps } from "./restrictions";
import useDonationConf from "../hooks/useDonationConf";
import DonationOfflineSection, {
  useDonationOffline,
} from "../../ui/Feature/OfflineDonations";
import ErrorsOrWarningsWrapper from "./ErrorsOrWarningsWrapper";
import RestrictionsWrapper from "./RestrictionsWrapper";

const getMandatoryEnv = require("sites-common/utils/getMandatoryEnv");

const { donationServiceConfig } = getMandatoryEnv(["donationServiceConfig"]);

const OFFLINE_DONATION_INITIAL_STATE = {
  showOfflineSection: false,
  offlineSectionDetails: {
    payment_option: "Donation by NEFT/RTGS",
    donationByCheque: {
      chequeNo: "",
      chequeDate: null,
      chequeBankName: "",
    },
    donationByNEFT: {
      neftBankName: "",
      ifscCode: "",
      accountNo: "",
    },
  },
};

const updateValidateState = (donationStateOffline, donationConfDetails) => {
  let offlineDonationState = null;

  const { enableOffline } = donationConfDetails;

  if (
    enableOffline &&
    donationStateOffline &&
    donationStateOffline.showOfflineSection
  ) {
    offlineDonationState = donationStateOffline;
  }

  return { offlineDonationState };
};

const doValidateSubmit = ({
  country,
  systemInfo,
  donorInfo,
  amount,
  currency,
  onErrors,
  onWarnings,
  setLoading,
  donationStateOffline,
  onSuccess,
  overrideWarnings = [],
  donationConfDetails,
  conf,
}) => {
  const { offlineDonationState } = updateValidateState(
    donationStateOffline,
    donationConfDetails
  );

  const { errors, warnings } = validateProfile(
    country,
    donorInfo,
    amount,
    currency,
    false,
    false,
    offlineDonationState
  );
  if (errors.length) {
    // console.error(errors);
    onErrors(errors);
    onWarnings(warnings);
    return;
  }

  if (warnings) {
    const pendingWarnings = warnings.filter(
      (w) => !overrideWarnings.includes(w)
    );
    if (pendingWarnings.length) {
      onWarnings(pendingWarnings);
      return;
    }
  }

  const formData = {
    donationServiceConfig,
    ...systemInfo,
    ...donorInfo,
    currency,
    amount,
  };

  const restrictions = checkRestrictions({
    conf,
    country,
    amount,
    donorInfo,
    donationId: systemInfo.donation_id,
  });

  if (restrictions.length) {
    return;
  }

  if (offlineDonationState) {
    donationsOfflineService(
      formData,
      setLoading,
      (s) => onErrors([s]),
      offlineDonationState
    );
  } else {
    submitDonationRequest(formData, setLoading, onSuccess, (s) =>
      onErrors([s])
    );
  }
};

const OfflineWidget = ({
  formDonationId,
  initialAmount,
  initialCurrency,
  projectId,
  packageDescription,
  country,
  initialProfile,
  paymentSuccessUrl,
  paymentFailureUrl,
  showOtherCitizen,
  enableOffline,
  isOfflineReadOnly,
  enableSignInOption,
  meditationPlaceFieldAvl,
}) => {
  const { theme } = useThemeUI();
  const [to] = useState(initialCurrency);
  const [toAmount, setToAmount] = useState(initialAmount);
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);

  const [donationStateOffline, setDonationStateOffline] = useState(
    enableOffline ? OFFLINE_DONATION_INITIAL_STATE : ""
  );
  const donationOfflineSection = useDonationOffline({
    ...donationStateOffline,
    isOfflinePage: paymentSuccessUrl.includes("offline"),
    country,
    onChange: (val) => {
      setDonationStateOffline(val);
      setErrors([]);
      setWarnings([]);
    },
  });

  const donationConfDetails = DonationConfProps(formDonationId);
  const [donorInfo, setDonorInfo] = useState({});
  const [loading, setLoading] = useState(false);

  const [editDonorField, setEditDonorField] = useState(false);
  const { numProfiles } = useDonorProfiles(country);

  const { conf } = useDonationConf();

  const [trk, setTrk] = usePersist("donation-trk", {});
  const onSuccess = useCallback(
    (d) => {
      setTrk({
        ...trk,
        [d.donation.payment.trackingId]: d.donation.userEmailAddress,
      });
    },
    [trk, setTrk]
  );

  const containerStyle = {
    mb: 1,
    borderBottom: `thin ${theme.colors.primary} solid`,
  };

  useEffect(() => {
    // Fix for scroll down while focusing on form fields in mobile (IOS and Android)
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const systemInfo = useMemo(
    () => ({
      donation_id: formDonationId,
      projects: [projectId],
      packageInfo: packageDescription,
      paymentSuccessUrl,
      paymentFailureUrl,
    }),
    [formDonationId, projectId, packageDescription]
  );

  const resetDonorInfo = useCallback(() => {
    setDonorInfo(initialProfile);
    setErrors([]);
    setWarnings([]);
  }, [initialProfile]);

  const setDonorInfoAndTriggerPay = useCallback(
    (p) => {
      setDonorInfo(p);
      setEditDonorField(false);

      if (p) {
        doValidateSubmit({
          donationServiceConfig,
          country,
          systemInfo,
          donorInfo: p,
          amount: toAmount,
          currency: to,
          onErrors: setErrors,
          onWarnings: setWarnings,
          setLoading,
          onSuccess,
          donationStateOffline,
          overrideWarnings: [],
          donationConfDetails,
          conf,
        });
      }
    },
    [toAmount, to, country, onSuccess, systemInfo]
  );

  const validateAndSubmit = useCallback(() => {
    doValidateSubmit({
      donationServiceConfig,
      country,
      systemInfo,
      donorInfo,
      amount: toAmount,
      currency: to,
      onErrors: setErrors,
      onWarnings: setWarnings,
      setLoading,
      onSuccess,
      donationStateOffline,
      overrideWarnings: [],
      donationConfDetails,
      conf,
    });
  }, [
    donorInfo,
    toAmount,
    to,
    setErrors,
    setWarnings,
    setLoading,
    onSuccess,
    country,

    donationStateOffline,
    systemInfo,
  ]);

  const validateAndSubmitOW = useCallback(() => {
    doValidateSubmit({
      donationServiceConfig,
      country,
      systemInfo,
      donorInfo,
      amount: toAmount,
      currency: to,
      onErrors: setErrors,
      onWarnings: setWarnings,
      setLoading,
      onSuccess,
      donationStateOffline,
      overrideWarnings: ["warning-in-pan", "warning-in-identity"],
      donationConfDetails,
      conf,
    });
  }, [
    donorInfo,
    toAmount,
    to,
    setErrors,
    setWarnings,
    setLoading,
    onSuccess,
    country,
    donationStateOffline,
    systemInfo,
  ]);

  // profileModes = ["restrictions", "selection-box", "editor-box", "warnings-box"];

  const restrictions = checkRestrictions({
    conf,
    country,
    amount: toAmount,
    donorInfo,
    donationId: formDonationId,
  });

  let show = "";
  if (restrictions.length > 0) {
    show = "restrictions";
  } else if (numProfiles > 0 && !editDonorField && warnings.length === 0) {
    show = "selection-box";
  } else if (!!editDonorField || (numProfiles === 0 && warnings.length === 0)) {
    show = "editor-box";
  } else {
    show = "warnings-box";
  }

  const openInfoEditor = useCallback(() => {
    setErrors([]);
    setWarnings([]);
    setEditDonorField("pan");
  }, []);

  const ButtonPay = useCallback(
    ({ extraText, ...restProps }) => (
      <div>
        <Button sx={{ ...theme.buttons.variant1 }} {...restProps}>
          {" "}
          {loading && <Spinner size={16} sx={{ color: "text" }} />}
          Confirm the details
        </Button>
        <Tos country={country} sx={{ color: "text", fontWeight: "600" }} />
      </div>
    ),
    [to, toAmount, loading, country, theme.buttons.variant1]
  );

  const AfterSelection = useCallback(
    () => (
      <div sx={{ mt: 5 }}>
        {errors.length > 0 && (
          <div sx={theme.messages.variantEW}>
            <div sx={{ color: "errortext" }}>
              <Grid gap={1} columns={["1fr 7fr"]}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FontAwesomeIcon
                    icon="exclamation-circle"
                    sx={{ fontSize: "25px" }}
                  />
                </Box>
                <Box>
                  <Grid gap={0}>
                    <Box>
                      <h4 sx={{ mb: 0 }}>
                        <b>Form Errors.</b>
                      </h4>
                    </Box>
                    <Box>
                      <h5>Please fix to continue!</h5>
                    </Box>
                  </Grid>
                </Box>
              </Grid>
            </div>
            <ErrorsOrWarnings errors={errors} warnings={warnings} />
          </div>
        )}
        {donorInfo.name && (
          <ButtonPay
            disabled={loading || errors.length > 0}
            onClick={validateAndSubmit}
          />
        )}
      </div>
    ),
    [
      errors,
      warnings,
      loading,
      validateAndSubmit,
      donorInfo,
      theme.messages.variantEW,
    ]
  );

  return (
    <Container sx={{ maxWidth: "320px", pb: 3, margin: "0 auto" }}>
      <div sx={{ mb: 1 }}>
        {enableOffline && (
          <DonationOfflineSection
            {...donationOfflineSection}
            donationId={systemInfo.donation_id}
            country={country}
            isReadOnly={isOfflineReadOnly}
          />
        )}
        <InputWithIconAndLabel
          sx={{ mb: 5 }}
          variant="border-bottom"
          name="Amount"
          value={toAmount}
          type="number"
          label={`${initialCurrency}:`}
          onChange={(x) => {
            setToAmount(x);
            setErrors([]);
            setWarnings([]);
          }}
        />
      </div>
      <RestrictionsWrapper
        show={show}
        country={country}
        donorInfo={donorInfo}
        resetDonorInfo={resetDonorInfo}
        restrictions={restrictions}
        meditationPlaceFieldAvl={meditationPlaceFieldAvl}
      />
      {show === "selection-box" && (
        <ProfileSelector
          containerStyle={containerStyle}
          country={country}
          initProfile={initialProfile}
          donorInfo={donorInfo}
          setDonorInfo={setDonorInfo}
          resetDonorInfo={resetDonorInfo}
          AfterSelection={AfterSelection}
          showOtherCitizen={showOtherCitizen}
          enableSignInOption={enableSignInOption}
          donationAmount={toAmount}
          meditationPlaceFieldAvl={meditationPlaceFieldAvl}
        />
      )}

      {show === "editor-box" && (
        <div>
          <div sx={{ color: "secondary", mb: 1 }}>Donor Information</div>
          <EditProfile
            country={country}
            ButtonPay={ButtonPay}
            value={editDonorField ? donorInfo : initialProfile}
            setValue={setDonorInfoAndTriggerPay}
            containerStyle={containerStyle}
            autoFocusName={editDonorField || "name"}
            showOtherCitizen={showOtherCitizen}
            enableSignInOption={enableSignInOption}
            donationAmount={toAmount}
            meditationPlaceFieldAvl={meditationPlaceFieldAvl}
          />
        </div>
      )}
      <ErrorsOrWarningsWrapper
        show={show}
        openInfoEditor={openInfoEditor}
        ButtonPay={ButtonPay}
        validateAndSubmitOW={validateAndSubmitOW}
        country={country}
        donorInfo={donorInfo}
        resetDonorInfo={resetDonorInfo}
        AfterSelection={AfterSelection}
        errors={errors}
        warnings={warnings}
        initialCurrency={initialCurrency}
        toAmount={toAmount}
        loading={loading}
        validateAndSubmit={validateAndSubmit}
        meditationPlaceFieldAvl={meditationPlaceFieldAvl}
      />
    </Container>
  );
};

OfflineWidget.propTypes = {
  formDonationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  initialAmount: PropTypes.number,
  initialCurrency: PropTypes.string,
  projectId: PropTypes.string,
  packageDescription: PropTypes.string,
  country: PropTypes.string,
  initialProfile: PropTypes.shape({}),
  enableOffline: PropTypes.bool,
  paymentSuccessUrl: PropTypes.string.isRequired,
  paymentFailureUrl: PropTypes.string.isRequired,
  showOtherCitizen: PropTypes.bool,
  isOfflineReadOnly: PropTypes.bool,
  enableSignInOption: PropTypes.bool,
  meditationPlaceFieldAvl: PropTypes.bool,
};

OfflineWidget.defaultProps = {
  initialAmount: 0,
  initialCurrency: "INR",
  projectId: "",
  packageDescription: "",
  country: "in",
  initialProfile: {},
  enableOffline: false,
  showOtherCitizen: true,
  isOfflineReadOnly: false,
  enableSignInOption: true,
  meditationPlaceFieldAvl: false,
};
export default OfflineWidget;
